import { useEffect } from 'react';
import { useToast } from '@efira/ui';

import { ApiError } from './client';

/**
 * Ни одна мутация не завершается тихо.
 *
 * ПРАВИЛО ПРОЕКТА, а не свойство одного экрана: неуспешный ответ сервера
 * обязан стать видимой пользователю ошибкой. Запись в консоли браузера не
 * считается: её читает разработчик через неделю, а решение принимает
 * пользователь сейчас.
 *
 * Устроено через `MutationCache`, а не через `onError` у каждой мутации,
 * ровно по той же причине, по которой заголовок ставится в одном месте:
 * правило, требующее внимательности на каждом вызове, однажды будет забыто -
 * и забытым окажется именно тот вызов, который сломается.
 *
 * Тосты подписываются на общий поток ошибок: QueryClient живёт вне React, а
 * показать тост может только компонент внутри провайдера.
 */

type Listener = (error: ApiError) => void;

const listeners = new Set<Listener>();

/** Вызывается из QueryClient при любой неуспешной мутации. */
export function reportApiError(error: unknown): void {
  const apiError =
    error instanceof ApiError
      ? error
      : new ApiError(0, '', error instanceof Error ? error.message : 'Неизвестная ошибка');

  for (const listener of listeners) {
    listener(apiError);
  }
}

export function onApiError(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Показывает ошибки мутаций тостом.
 *
 * Монтируется один раз внутри `ToastProvider`. Сессионный отказ (401) не
 * повторяется тостом на каждый запрос: он означает «войдите заново», и это
 * состояние экрана, а не событие.
 */
export function ApiErrorToasts() {
  const toast = useToast();

  useEffect(
    () =>
      onApiError((error) => {
        toast.show({
          tone: 'error',
          title: 'Не выполнено',
          text: error.message,
          // Ошибка висит дольше успеха: её надо успеть прочитать.
          duration: 8000,
        });
      }),
    [toast],
  );

  return null;
}
